import { type DeviceModel, deviceFinish, deviceModel } from "./catalog";
import { type DeviceFace, deviceFaces } from "./draw";

/**
 * 3D view of a device mockup (docs/19-mockups.md). Every device is made of
 * flat slabs (a rounded rectangle with a thickness): a phone is one slab, a
 * laptop a lid and a base. The view turns them by the element's tilt, projects
 * them with a perspective camera in front and fits the result into the
 * element's box. The renderer draws each visible face with a CSS `matrix3d`
 * (a plain 2D `matrix` when the face looks straight at the camera, so flat
 * mockups stay vector in PDFs) and the sides as 2D outlines behind it.
 */

export interface DeviceTilt {
  /** Top edge away from (positive) or towards the viewer, degrees. */
  x: number;
  /** Turned so the screen faces right (positive) or left, degrees. */
  y: number;
  /** Spin in the screen plane, degrees (lying devices). */
  z: number;
}

export const DEVICE_TILT_LIMIT = { x: 75, y: 75, z: 180 } as const;

/** What the layout needs of a device element. */
export interface DeviceProps {
  device: string;
  finish?: string | undefined;
  landscape: boolean;
  tilt?: DeviceTilt | undefined;
  url?: string | undefined;
}

type Vec3 = [number, number, number];
/** Row-major 4 × 4 matrix. */
type Mat4 = number[][];

interface Slab {
  width: number;
  height: number;
  radius: number;
  depth: number;
  /** Local (u, v) → device space: origin + u·U + v·V; the face looks along U × V. */
  origin: Vec3;
  u: Vec3;
  v: Vec3;
  face: DeviceFace;
  side: string;
}

export interface DeviceFacePart {
  kind: "face";
  face: DeviceFace;
  /** Size of the face in element pixels (the transform maps it into the box). */
  width: number;
  height: number;
  /** CSS transform (origin: top left of the face). */
  transform: string;
  /** The same as a row-major 4 × 4 matrix: face pixels (x, y, 0, 1) → homogeneous box pixels. */
  matrix: number[][];
}

export interface DeviceOutlinePart {
  kind: "outline";
  /** Path data in element pixels. */
  d: string;
  fill: string;
}

export type DevicePart = DeviceFacePart | DeviceOutlinePart;

export interface DeviceLayout {
  model: DeviceModel;
  parts: DevicePart[];
  /** Element pixels per face unit (mm). */
  scale: number;
}

const RAD = Math.PI / 180;

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function add(a: Vec3, b: Vec3, scale = 1): Vec3 {
  return [a[0] + b[0] * scale, a[1] + b[1] * scale, a[2] + b[2] * scale];
}

function multiply(a: Mat4, b: Mat4): Mat4 {
  return a.map((row) =>
    [0, 1, 2, 3].map((column) =>
      row.reduce((sum, value, index) => sum + value * (b[index]?.[column] ?? 0), 0),
    ),
  );
}

/** Rotation: spin (z) first, then turn (y), then tilt (x). CSS axes: y down, z to the viewer. */
function rotation(tilt: DeviceTilt): Mat4 {
  const [ax, ay, az] = [tilt.x * RAD, tilt.y * RAD, tilt.z * RAD];
  const rx: Mat4 = [
    [1, 0, 0, 0],
    [0, Math.cos(ax), -Math.sin(ax), 0],
    [0, Math.sin(ax), Math.cos(ax), 0],
    [0, 0, 0, 1],
  ];
  const ry: Mat4 = [
    [Math.cos(ay), 0, Math.sin(ay), 0],
    [0, 1, 0, 0],
    [-Math.sin(ay), 0, Math.cos(ay), 0],
    [0, 0, 0, 1],
  ];
  const rz: Mat4 = [
    [Math.cos(az), -Math.sin(az), 0, 0],
    [Math.sin(az), Math.cos(az), 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1],
  ];
  return multiply(rx, multiply(ry, rz));
}

function apply(m: Mat4, p: Vec3): Vec3 {
  return [0, 1, 2].map(
    (row) =>
      (m[row]?.[0] ?? 0) * p[0] +
      (m[row]?.[1] ?? 0) * p[1] +
      (m[row]?.[2] ?? 0) * p[2] +
      (m[row]?.[3] ?? 0),
  ) as Vec3;
}

/** Points along a rounded rectangle (local face coordinates). */
function outlinePoints(width: number, height: number, radius: number): [number, number][] {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  const corners: [number, number, number][] = [
    [width - r, r, -90],
    [width - r, height - r, 0],
    [r, height - r, 90],
    [r, r, 180],
  ];
  const steps = r > 0 ? 8 : 0;
  const points: [number, number][] = [];
  for (const [cx, cy, start] of corners) {
    for (let step = 0; step <= steps; step++) {
      const angle = (start + (90 * step) / Math.max(1, steps)) * RAD;
      points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
    }
  }
  return points;
}

/** Convex hull (monotone chain) of 2D points. */
function hull(points: [number, number][]): [number, number][] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (sorted.length < 3) return sorted;
  const turn = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: [number, number][] = [];
  for (const point of sorted) {
    while (
      lower.length >= 2 &&
      turn(
        lower[lower.length - 2] as [number, number],
        lower[lower.length - 1] as [number, number],
        point,
      ) <= 0
    )
      lower.pop();
    lower.push(point);
  }
  const upper: [number, number][] = [];
  for (const point of [...sorted].reverse()) {
    while (
      upper.length >= 2 &&
      turn(
        upper[upper.length - 2] as [number, number],
        upper[upper.length - 1] as [number, number],
        point,
      ) <= 0
    )
      upper.pop();
    upper.push(point);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/**
 * Slabs of a device, back to front (the painting order; the tilt limits keep
 * it valid): phones one, laptops lid and base, desktops stand and screen.
 */
function slabsFor(props: DeviceProps, contentAspect: number | null | undefined): Slab[] {
  const model = deviceModel(props.device);
  const finish = deviceFinish(model, props.finish);
  const faces = deviceFaces(model, finish, {
    landscape: props.landscape && model.spec.kind === "slate",
    contentAspect,
    ...(props.url ? { url: props.url } : {}),
  });
  const spec = model.spec;
  const X: Vec3 = [1, 0, 0];
  const Y: Vec3 = [0, 1, 0];
  const Z: Vec3 = [0, 0, 1];
  const main = faces.main;
  const upright = (face: DeviceFace, radius: number, depth: number, origin?: Vec3): Slab => ({
    width: face.width,
    height: face.height,
    radius,
    depth,
    origin: origin ?? [-face.width / 2, -face.height / 2, 0],
    u: X,
    v: Y,
    face,
    side: finish.side,
  });
  switch (spec.kind) {
    case "slate":
      return [upright(main, spec.radius, spec.depth)];
    case "browser":
      return [upright(main, 14, 0)];
    case "laptop": {
      // Hinge at the origin: the lid stands on the back edge of the base.
      const lid = upright(main, Math.min(spec.radius, 8), spec.lidDepth, [
        -main.width / 2,
        -main.height,
        0,
      ]);
      const deck = faces.deck as DeviceFace;
      const base: Slab = {
        width: deck.width,
        height: deck.height,
        radius: spec.radius,
        depth: spec.baseDepth,
        origin: [-deck.width / 2, 0, -spec.lidDepth],
        u: X,
        v: Z,
        face: deck,
        side: finish.side,
      };
      return [lid, base];
    }
    case "desktop": {
      const neck = faces.neck as DeviceFace;
      const foot = faces.foot as DeviceFace;
      const behind = -spec.depth - 18;
      const floor = main.height / 2 + spec.stand.height;
      // Back to front: neck, foot, screen.
      return [
        {
          ...upright(neck, 2, 10, [-neck.width / 2, floor - neck.height, behind]),
          side: finish.side,
        },
        {
          width: foot.width,
          height: foot.height,
          radius: 10,
          depth: 5,
          origin: [-foot.width / 2, floor, behind - foot.height * 0.55],
          u: X,
          v: Z,
          face: foot,
          side: finish.side,
        },
        upright(main, spec.radius, spec.depth),
      ];
    }
  }
}

/** Perspective distance: five times the device's longest side (a mild, natural perspective). */
function cameraDistance(slabs: Slab[]): number {
  return 5 * Math.max(...slabs.map((slab) => Math.max(slab.width, slab.height)));
}

interface Projected {
  slab: Slab;
  front: [number, number][];
  back: [number, number][];
  frontVisible: boolean;
}

/**
 * Height of the camera. Straight on it looks at the middle; a laptop seen
 * straight on is looked at from the hinge (lid and a thin edge of the base,
 * the classic front view) and from above the keyboard once it is turned.
 */
function cameraHeight(props: DeviceProps, tilt: DeviceTilt): number {
  const model = deviceModel(props.device);
  if (model.spec.kind !== "laptop") return 0;
  const turned = Math.min(1, Math.max(Math.abs(tilt.x), Math.abs(tilt.y), Math.abs(tilt.z)) / 12);
  return -model.spec.lidHeight * 0.45 * turned;
}

function project(props: DeviceProps, contentAspect: number | null | undefined) {
  const slabs = slabsFor(props, contentAspect);
  const tilt = props.tilt ?? { x: 0, y: 0, z: 0 };
  const lift: Mat4 = [
    [1, 0, 0, 0],
    [0, 1, 0, -cameraHeight(props, tilt)],
    [0, 0, 1, 0],
    [0, 0, 0, 1],
  ];
  // Turned device, moved so the camera sits on the z axis.
  const rotate = multiply(lift, rotation(tilt));
  const distance = cameraDistance(slabs);
  const toScreen = (point: Vec3): [number, number] => {
    const [x, y, z] = apply(rotate, point);
    const w = 1 - z / distance;
    return [x / w, y / w];
  };
  const projected: Projected[] = slabs.map((slab) => {
    const normal = cross(slab.u, slab.v);
    const local = (u: number, v: number, n: number): Vec3 =>
      add(add(add(slab.origin, slab.u, u), slab.v, v), normal, n);
    const outline = outlinePoints(slab.width, slab.height, slab.radius);
    const front = outline.map(([u, v]) => toScreen(local(u, v, 0)));
    const back = slab.depth > 0 ? outline.map(([u, v]) => toScreen(local(u, v, -slab.depth))) : [];
    const faceCenter = apply(rotate, local(slab.width / 2, slab.height / 2, 0));
    const turned = apply(rotation(tilt), normal);
    const toCamera: Vec3 = [-faceCenter[0], -faceCenter[1], distance - faceCenter[2]];
    const facing =
      (turned[0] ?? 0) * toCamera[0] +
      (turned[1] ?? 0) * toCamera[1] +
      (turned[2] ?? 0) * toCamera[2];
    return { slab, front, back, frontVisible: facing > 1e-6 };
  });
  return { projected, rotate, distance };
}

function bounds(projected: Projected[]) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const entry of projected) {
    for (const [x, y] of [...entry.front, ...entry.back]) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  // Buttons stick out a little.
  const pad = Math.max(maxX - minX, maxY - minY) * 0.012;
  return {
    x: minX - pad,
    y: minY - pad,
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2,
  };
}

/** Width / height of the whole device in this view (the element box should match it). */
export function deviceAspect(props: DeviceProps, contentAspect?: number | null): number {
  const box = bounds(project(props, contentAspect).projected);
  return box.width / box.height;
}

/** Size of the device in face units (mm) in this view: for keeping the scale when the view changes. */
export function deviceExtent(
  props: DeviceProps,
  contentAspect?: number | null,
): { width: number; height: number } {
  const box = bounds(project(props, contentAspect).projected);
  return { width: box.width, height: box.height };
}

const n = (value: number) => Number(value.toFixed(4));

function pathFrom(points: [number, number][]): string {
  return `${points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${n(x)} ${n(y)}`).join("")}Z`;
}

/**
 * Parts to draw for a device element in a box of `width` × `height` pixels,
 * back to front. `contentAspect` is the screenshot's height / width (browser
 * windows take its shape).
 */
export function layoutDevice(
  props: DeviceProps,
  box: { width: number; height: number },
  contentAspect?: number | null,
): DeviceLayout {
  const model = deviceModel(props.device);
  const { projected, rotate, distance } = project(props, contentAspect);
  const extent = bounds(projected);
  const scale = Math.min(box.width / extent.width, box.height / extent.height);
  const offsetX = box.width / 2 - scale * (extent.x + extent.width / 2);
  const offsetY = box.height / 2 - scale * (extent.y + extent.height / 2);
  const toBox = ([x, y]: [number, number]): [number, number] => [
    x * scale + offsetX,
    y * scale + offsetY,
  ];
  const parts: DevicePart[] = [];
  for (const entry of projected) {
    const { slab } = entry;
    if (entry.back.length > 0) {
      parts.push({
        kind: "outline",
        d: pathFrom(hull([...entry.front, ...entry.back]).map(toBox)),
        fill: slab.side,
      });
    }
    if (!entry.frontVisible) continue;
    // Face pixels (a, b) → device space → turned → perspective → box pixels.
    const normal = cross(slab.u, slab.v);
    const place: Mat4 = [
      [slab.u[0] / scale, slab.v[0] / scale, normal[0], slab.origin[0]],
      [slab.u[1] / scale, slab.v[1] / scale, normal[1], slab.origin[1]],
      [slab.u[2] / scale, slab.v[2] / scale, normal[2], slab.origin[2]],
      [0, 0, 0, 1],
    ];
    const perspective: Mat4 = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, -1 / distance, 1],
    ];
    const fit: Mat4 = [
      [scale, 0, 0, offsetX],
      [0, scale, 0, offsetY],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];
    const m = multiply(fit, multiply(perspective, multiply(rotate, place)));
    parts.push({
      kind: "face",
      face: slab.face,
      width: slab.face.width * scale,
      height: slab.face.height * scale,
      transform: cssTransform(m),
      matrix: m,
    });
  }
  return { model, parts, scale };
}

/** CSS transform of a face: 2D `matrix` when it has no perspective, else `matrix3d`. */
function cssTransform(m: Mat4): string {
  const row3 = m[3] as number[];
  const w = row3[3] as number;
  const at = (row: number, column: number) => m[row]?.[column] ?? 0;
  if (Math.abs(row3[0] ?? 0) < 1e-9 && Math.abs(row3[1] ?? 0) < 1e-9) {
    const values = [at(0, 0), at(1, 0), at(0, 1), at(1, 1), at(0, 3), at(1, 3)].map((value) =>
      n(value / w),
    );
    return `matrix(${values.join(",")})`;
  }
  // matrix3d is column-major.
  const values: number[] = [];
  for (let column = 0; column < 4; column++) {
    for (let row = 0; row < 4; row++) values.push(at(row, column));
  }
  return `matrix3d(${values.map((value) => Number(value.toPrecision(10))).join(",")})`;
}

export type DevicePoint = { x: number; y: number };

/** Where the screen of a device lies in its box (for putting a video onto it). */
export interface DeviceScreenQuad {
  /** Corners of the upright screen picture in box pixels (after the perspective). */
  topLeft: DevicePoint;
  topRight: DevicePoint;
  bottomRight: DevicePoint;
  bottomLeft: DevicePoint;
  /** Size of the screen on its face in box pixels (the flat picture before the perspective). */
  width: number;
  height: number;
  /** The screen is an upright rectangle (front view without spin): no warping needed. */
  rectangular: boolean;
}

/** Screen corners of a device in a box of `width` × `height` pixels; null when the screen faces away. */
export function deviceScreenQuad(
  props: DeviceProps,
  box: { width: number; height: number },
  contentAspect?: number | null,
): DeviceScreenQuad | null {
  const layout = layoutDevice(props, box, contentAspect);
  const part = layout.parts.find(
    (entry): entry is DeviceFacePart => entry.kind === "face" && entry.face.screen !== null,
  );
  const screen = part?.face.screen;
  if (!part || !screen) return null;
  const k = part.width / part.face.width;
  const project = (u: number, v: number): DevicePoint => {
    const [x, y, , w] = [0, 1, 2, 3].map(
      (row) =>
        (part.matrix[row]?.[0] ?? 0) * u +
        (part.matrix[row]?.[1] ?? 0) * v +
        (part.matrix[row]?.[3] ?? 0),
    ) as [number, number, number, number];
    return { x: x / w, y: y / w };
  };
  const left = screen.x * k;
  const top = screen.y * k;
  const right = (screen.x + screen.width) * k;
  const bottom = (screen.y + screen.height) * k;
  const quad = {
    topLeft: project(left, top),
    topRight: project(right, top),
    bottomRight: project(right, bottom),
    bottomLeft: project(left, bottom),
  };
  const same = (a: number, b: number) => Math.abs(a - b) < 0.01;
  const rectangular =
    same(quad.topLeft.y, quad.topRight.y) &&
    same(quad.bottomLeft.y, quad.bottomRight.y) &&
    same(quad.topLeft.x, quad.bottomLeft.x) &&
    same(quad.topRight.x, quad.bottomRight.x) &&
    quad.topRight.x > quad.topLeft.x &&
    quad.bottomLeft.y > quad.topLeft.y;
  return { ...quad, width: right - left, height: bottom - top, rectangular };
}

/** Bounding box of a screen quad (box pixels). */
export function quadBounds(quad: DeviceScreenQuad): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const points = [quad.topLeft, quad.topRight, quad.bottomRight, quad.bottomLeft];
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

export interface DevicePerspective {
  id: string;
  label: string;
  tilt: DeviceTilt;
}

const STRAIGHT: DevicePerspective = { id: "front", label: "Gerade", tilt: { x: 0, y: 0, z: 0 } };

/** Ready-made views per kind of device (the tilt sliders allow anything in between). */
export function devicePerspectives(device: string): DevicePerspective[] {
  const kind = deviceModel(device).spec.kind;
  if (kind === "laptop") {
    return [
      STRAIGHT,
      { id: "right", label: "Nach rechts", tilt: { x: -10, y: 30, z: 0 } },
      { id: "left", label: "Nach links", tilt: { x: -10, y: -30, z: 0 } },
      { id: "hero", label: "Schräg", tilt: { x: -16, y: -24, z: 0 } },
      { id: "above", label: "Von oben", tilt: { x: -28, y: 0, z: 0 } },
    ];
  }
  if (kind === "desktop") {
    return [
      STRAIGHT,
      { id: "right", label: "Nach rechts", tilt: { x: 0, y: 26, z: 0 } },
      { id: "left", label: "Nach links", tilt: { x: 0, y: -26, z: 0 } },
      { id: "hero", label: "Schräg", tilt: { x: -12, y: -22, z: 0 } },
    ];
  }
  return [
    STRAIGHT,
    { id: "right", label: "Nach rechts", tilt: { x: 0, y: 28, z: 0 } },
    { id: "left", label: "Nach links", tilt: { x: 0, y: -28, z: 0 } },
    { id: "hero", label: "Schräg", tilt: { x: 12, y: -22, z: 4 } },
    { id: "float", label: "Schwebend", tilt: { x: -14, y: 18, z: -8 } },
    { id: "lying", label: "Liegend", tilt: { x: 52, y: 0, z: 32 } },
  ];
}

/** The ready-made view the tilt equals, if any. */
export function matchingPerspective(device: string, tilt: DeviceTilt): string | null {
  const same = (a: DeviceTilt) =>
    Math.abs(a.x - tilt.x) < 0.5 && Math.abs(a.y - tilt.y) < 0.5 && Math.abs(a.z - tilt.z) < 0.5;
  return devicePerspectives(device).find((entry) => same(entry.tilt))?.id ?? null;
}
